import type { CSSProperties } from 'react'
import type { Company } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { Icon } from '@/shared/ui'
import { CompanyAvatar } from './CompanyAvatar'
import s from './CompanyCard.module.css'

type CompanyCardProps = {
  company: Pick<Company, 'name' | 'city' | 'domain' | 'initials' | 'colorKey'>
  active?: boolean
  onClick?: () => void
  /** Пока летит переключение — не дать запустить второе поверх первого. */
  disabled?: boolean
  /** Ступенька pop-in считается на вызывающей стороне — только она знает индекс в списке. */
  style?: CSSProperties
}

/**
 * Строка компании в поповере свитчера (prototype/Prototype.dc.html:141-161).
 *
 * Активная — статичная плашка (галочка, подсветка `--accent-soft`, без анимации и без клика:
 * в макете у неё нет ни `onClick`, ни ступеньки pop-in). Остальные — кликабельные кнопки
 * с шевроном и pop-in по образцу макета. Реальная кнопка (`<button>`), а не `<a href="#">`
 * как в макете: это действие («переключить компанию»), а не переход по ссылке — тот же
 * принцип, по которому Task 17 меняет кнопки навигации макета на настоящие `<Link>`, только
 * в обратную сторону.
 */
export function CompanyCard({ company, active, onClick, disabled, style }: CompanyCardProps) {
  const text = (
    <span className={s.text}>
      <span className={cn(s.name, active && s.nameActive)}>{company.name}</span>
      <span className={s.domain}>
        {company.city} · {company.domain}
      </span>
    </span>
  )

  if (active) {
    return (
      <div className={cn(s.root, s.active)} style={style}>
        <CompanyAvatar company={company} size={32} />
        {text}
        <Icon name="check" size={16} strokeWidth={2.6} className={s.check} />
      </div>
    )
  }

  return (
    <button
      type="button"
      className={s.root}
      style={style}
      onClick={onClick}
      disabled={disabled}
    >
      <CompanyAvatar company={company} size={32} />
      {text}
      <Icon name="chevron-right" size={14} className={s.chevron} />
    </button>
  )
}
